import { filterByDocumentType } from 'Utility/documentHandling';
import { exists, hasAttributes, isPartialDate, sortPartialDate } from 'neuro-utils';
import { updrs_iii } from 'neuro-schemas';
import { path } from 'Utility/ramdaReplacement';

export type TUpdrsDocumentType = SafeExtract<
  TDocumentType,
  'updrs_iii' | 'updrs_v' | 'ftmtrs' | 'levodopatest' | 'dbsEffect'
>;

type TSecondaryDocumentType = SafeExtract<TDocumentType, 'dbs'>;

export type TRelevantDocumentType = TUpdrsDocumentType | TSecondaryDocumentType;

/**
 * Get first document that matches type and has some score value
 * @param {string} type - Document type
 * @param {TDocument[]|undefined} docs - All documents
 * @returns {TDocument|undefined} Matched document or undefined
 */
export const sortAndTakeFirst = <T extends TUpdrsDocumentType>(
  type: T,
  docs: IDocumentWithTypeV2<TUpdrsDocumentType>[],
): IDocumentWithTypeV2<T> | undefined => {
  switch (type) {
    case 'updrs_iii': {
      return filterByDocumentType(docs, 'updrs_iii')
        .sort((n1, n2) => sortPartialDate(n2.date, n1.date))
        .find(d => updrs_iii.calculateScore(d) !== null) as IDocumentWithTypeV2<typeof type> | undefined;
    }
    case 'dbsEffect': {
      return filterByDocumentType(docs, 'dbsEffect')
        .sort((n1, n2) => sortPartialDate(n2.date, n1.date))
        .at(0) as IDocumentWithTypeV2<typeof type> | undefined;
      //   (d._type === type &&
      //   isUpdrsComplete(getIIIDoc(path(['updrsIIIBeforeID'], d), documents), (d as IDBSEffect).testType) &&
      //   isUpdrsComplete(getIIIDoc(path(['updrsIIIAfterID'], d), documents), (d as IDBSEffect).testType)) ||
      // (d._type === type && !!(d as IDBSEffect).manualScore) ||
      // (d._type === type && (d as IDBSEffect).manualScore === 0),
    }
    case 'levodopatest': {
      return filterByDocumentType(docs, 'levodopatest')
        .sort((n1, n2) => sortPartialDate(n2.date, n1.date))
        .at(0) as IDocumentWithTypeV2<typeof type> | undefined;
      // isUpdrsComplete(getIIIDoc(path(['updrsIIIBeforeID'], d), documents), (d as ILevodopatest).testType) &&
      //   isUpdrsComplete(getIIIDoc(path(['updrsIIIAfterID'], d), documents), (d as ILevodopatest).testType)) ||
      // (d._type === type && !!(d as ILevodopatest).manualScore) ||
      // (d._type === type && (d as ILevodopatest).manualScore === 0),
    }
    default: {
      return filterByDocumentType(docs, type)
        .sort((n1, n2) => {
          const n1Date = hasAttributes(n1, ['date']) && isPartialDate(n1.date) ? n1.date : null;
          const n2Date = hasAttributes(n2, ['date']) && isPartialDate(n2.date) ? n2.date : null;
          return sortPartialDate(n2Date, n1Date);
        })
        .at(0);
    }
  }
};

/**
 * Get UPDRS III document from all documents by id
 * @param  {string|undefined} id - Document id
 * @param  {Array<TDocument>|undefined} documents - Documents array
 * @returns {IUPDRSIII|undefined} ID matched document
 */
export const getUPDRSIIIDoc = (
  id?: string | null,
  documents?: IDocumentWithTypeV2<TRelevantDocumentType>[],
): IUPDRSIII | undefined => (id && documents ? (documents.find(d => d._id === id) as IUPDRSIII) : undefined);

export const findLinkedDoc = (
  documents: IDocumentWithTypeV2<TRelevantDocumentType>[],
  targetId: string | null | undefined,
): IDocumentWithTypeV2<'updrs_iii'> | undefined =>
  targetId ? (documents.find(d => d._id === targetId) as IDocumentWithTypeV2<'updrs_iii'>) : undefined;

/**
 * Matches editing/viewing document id to all documents and returns type for the document
 * @param  {string} editing - Editing document id
 * @param  {TDocument[]} documents - All documents
 * @param  {string|undefined} viewing - Possible viewing document id
 * @returns {string|undefined} Document type
 */
export const docType = (
  editing: string,
  documents: IDocumentWithTypeV2<TRelevantDocumentType>[],
  viewing?: string,
): string | undefined =>
  editing || viewing
    ? (path(
        ['_type'],
        documents.find(d => d._id === editing || d._id === viewing),
      ) ?? undefined)
    : undefined;

export const ftmtrsPartA: Array<keyof IFTMTRS> = [
  'faceTremor',
  'tongueTremor',
  'voiceTremor',
  'headTremor',
  'rueTremor',
  'lueTremor',
  'trunkTremor',
  'rleTremor',
  'lleTremor',
  'orthostaticTremor',
];

const ftmtrsPartB = ['drawingA', 'drawingB', 'drawingC', 'pouring'];

export const ftmtrsPartC: Array<keyof IFTMTRS> = [
  'speaking',
  'eating',
  'drinking',
  'hygiene',
  'dressing',
  'writing',
  'working',
  'socialActivities',
];

type TScoreReturn<P> = P extends 'a' | 'c' ? number | null : { right: number | null; left: number | null } | null;

export const ftmtrsScore = <P = 'a' | 'b' | 'c'>(document: IFTMTRS, part: P): TScoreReturn<P> => {
  const someManualScoresExist =
    exists(document.manualSubtotalA) ||
    exists(document.manualSubtotalBright) ||
    exists(document.manualSubtotalBleft) ||
    exists(document.manualSubtotalC);

  // Part A or C
  if (part === 'a' || part === 'c') {
    if (part === 'a' && someManualScoresExist) return document.manualSubtotalA as TScoreReturn<P>;
    if (part === 'c' && someManualScoresExist) return document.manualSubtotalC as TScoreReturn<P>;

    const scores: Array<number | null> = (part === 'a' ? ftmtrsPartA : ftmtrsPartC).map(field =>
      Number(document[field]) || document[field] === 0 ? Number(document[field]) : null,
    );
    if (scores.some(sc => sc === null)) return null;
    return (scores as number[]).reduce((a, b) => a + b) as TScoreReturn<P>;
  }
  // Part B
  const rightSideScore = ftmtrsPartB.map(field => {
    const scoreName = (field + 'right') as keyof IFTMTRS;
    const scoreNumber = Number(document[scoreName]) || document[scoreName] === 0 ? Number(document[scoreName]) : null;
    return scoreNumber;
  });
  const leftSideScore = ftmtrsPartB.map(field => {
    const scoreName = (field + 'left') as keyof IFTMTRS;
    const scoreNumber = Number(document[scoreName]) || document[scoreName] === 0 ? Number(document[scoreName]) : null;
    return scoreNumber;
  });

  rightSideScore.push(document.handWriting ?? null);
  leftSideScore.push(document.handWriting ?? null);

  return {
    right: someManualScoresExist
      ? document.manualSubtotalBright
      : rightSideScore.some(sc => sc === null)
        ? null
        : (rightSideScore as number[]).reduce((a, b) => a + b),
    left: someManualScoresExist
      ? document.manualSubtotalBleft
      : leftSideScore.some(sc => sc === null)
        ? null
        : (leftSideScore as number[]).reduce((a, b) => a + b),
  } as TScoreReturn<P>;
};
